import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const badgeVariants = cva(
    'inline-flex items-center rounded-md border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
    {
        variants: {
            variant: {
                default: 'border-transparent bg-primary text-primary-foreground hover:bg-primary/90',
                secondary: 'border border-secondary/15 bg-secondary/10 text-secondary hover:bg-secondary/15',
                destructive: 'border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80',
                outline: 'text-foreground',
                success:
                    'border-transparent bg-emerald-100 text-emerald-800 hover:bg-emerald-100 dark:bg-emerald-950 dark:text-emerald-300',
                info: 'border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-50 dark:border-indigo-900 dark:bg-indigo-950 dark:text-indigo-300',
                danger: 'border-transparent bg-destructive/10 text-destructive hover:bg-destructive/10 dark:bg-destructive/20',
                scheduled:
                    'border-transparent bg-slate-200 text-slate-800 hover:bg-slate-200 dark:bg-secondary dark:text-secondary-foreground',
                canceled:
                    'border-slate-300 bg-slate-100 text-slate-700 hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200',
                published: 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950 dark:text-emerald-300',
                processing: 'border-indigo-200 bg-indigo-50 text-indigo-700 dark:border-indigo-900 dark:bg-indigo-950 dark:text-indigo-300',
                neutral: 'border-border bg-muted/60 text-muted-foreground',
                code: 'rounded-md border border-zinc-200 bg-zinc-100 font-mono text-[10.5px] text-zinc-500',
                warning:
                    'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-300',
                muted: 'border-border bg-muted text-muted-foreground',
                inverse: 'border-0 bg-white/10 text-white hover:bg-white/10'
            },
            size: {
                default: 'px-2.5 py-0.5 text-xs font-semibold',
                compact: 'px-3 py-1 text-[11px] font-medium',
                status: 'px-2.5 py-0.5 font-mono text-[11px] font-semibold uppercase tracking-[0.14em]'
            },
            shape: {
                default: 'rounded-md',
                pill: 'rounded-full'
            },
            appearance: {
                default: '',
                role: 'capitalize',
                medium: 'font-medium',
                onboarding: 'rounded-full px-3 py-1 text-xs font-medium',
                status: 'rounded-full px-2.5 py-0.5 font-mono text-[11px] font-medium transition-none hover:bg-inherit',
                compact: 'rounded-full px-2 py-0.5 font-mono text-[11px] font-medium'
            }
        },
        defaultVariants: {
            variant: 'default',
            size: 'default',
            shape: 'default'
        }
    }
);

interface BadgeProps extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof badgeVariants> {
    capitalize?: boolean;
}

function Badge({ className, variant, size, shape, appearance, capitalize = false, ...props }: BadgeProps) {
    return <div className={cn(badgeVariants({ variant, size, shape, appearance }), capitalize && 'capitalize', className)} {...props} />;
}

export { Badge, badgeVariants };
export type { BadgeProps };
